import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import Swal from 'sweetalert2';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06);
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
`;

const FilterLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RegisteredToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const RegisteredToggleButton = styled.button`
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? '#059669' : '#d1d5db')};
  background: ${(p) => {
    if (!p.$active) return '#ffffff';
    return p.$pending ? 'linear-gradient(90deg, #f97316, #ef4444)' : 'linear-gradient(90deg, #10b981, #059669)';
  }};
  color: ${(p) => (p.$active ? '#ffffff' : '#1f2937')};
  font-weight: 600;
  font-size: 14px;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: ${(p) => (p.$active ? '0 10px 15px -3px rgba(16, 185, 129, 0.3)' : 'none')};

  &:hover {
    border-color: ${(p) => (p.$active ? '#047857' : '#94a3b8')};
  }
`;

const RegisteredToggleHint = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const ClearToggleButton = styled.button`
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 8px;

  &:hover {
    color: #ef4444;
  }
`;


const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  tbody tr {
    transition: background-color 0.15s ease;
    
    &:nth-child(even) {
      background-color: #f9fafb;
    }
    
    &:hover {
      background-color: #f3f4f6;
    }
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 14px 16px;
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  color: #374151;
  vertical-align: middle;
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #d1d5db;
  background: #f3f4f6;
`;

const AvatarPlaceholder = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: 1px solid #d1d5db;
`;

const CounselorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => {
    if (p.$status === 'registered') return '#d1fae5';
    if (p.$status === 'in followup') return '#dbeafe';
    if (p.$status === 'plan to register') return '#fef3c7';
    if (p.$status === 'will not register') return '#fee2e2';
    return '#e5e7eb';
  }};
  color: ${(p) => {
    if (p.$status === 'registered') return '#065f46';
    if (p.$status === 'in followup') return '#1e40af';
    if (p.$status === 'plan to register') return '#92400e';
    if (p.$status === 'will not register') return '#991b1b';
    return '#374151';
  }};
`;

const currencyFormatter = new Intl.NumberFormat('en-LK', {
  style: 'currency',
  currency: 'LKR',
  maximumFractionDigits: 0,
});

function formatCurrency(value) {
  const numeric = Number.isFinite(value) ? value : 0;
  return currencyFormatter.format(Math.round(numeric));
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const ABSOLUTE_MEDIA_REGEX = /^(https?:)?\/\//i;
const BASE_HAS_API_SUFFIX = getImageURL('').endsWith('/api');

function buildStaffImageSrc(imagePath) {
  if (!imagePath) return '';
  const trimmed = String(imagePath).trim();
  if (!trimmed) return '';

  if (ABSOLUTE_MEDIA_REGEX.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }

  let normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  if (BASE_HAS_API_SUFFIX && normalized.startsWith('/api/')) {
    normalized = normalized.slice(4);
    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }
  }

  return getImageURL(normalized);
}

function formatStatusLabel(value) {
  if (!value) return 'Unknown';
  return value
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in followup', label: 'In Followup' },
  { value: 'will not register', label: 'Will Not Register' },
  { value: 'registered', label: 'Registered' },
  { value: 'registered as inter branch', label: 'Registered as Inter Branch' },
  { value: 'registered in another institution', label: 'Registered in Another Institution' },
  { value: 'plan to register', label: 'Plan To Register' },
];

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '28px',
    height: '28px',
    fontSize: '12px',
    borderColor: '#d1d5db',
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: '12px',
    zIndex: 9999,
  }),
  option: (provided) => ({
    ...provided,
    fontSize: '12px',
    padding: '6px 12px',
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '12px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0',
    padding: '0',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
};

const filterSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minWidth: '240px',
    minHeight: '42px',
    borderRadius: '6px',
    fontSize: '14px',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59,130,246,0.1)' : 'none',
    '&:hover': {
      borderColor: '#3b82f6',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 12px',
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: '#1f2937',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: '#9ca3af',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '14px',
    color: state.isSelected ? '#fff' : '#1f2937',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 20,
  }),
};

const normalizeStatusKey = (value) => {
  if (!value && value !== 0) return '';
  return String(value).trim().toLowerCase();
};

const CHECKLIST_ITEMS = [
  {
    id: 'photo-uploaded',
    field: 'isPhotoUploaded',
    label: 'Upload student photo',
    description: 'Capture or upload the student photo before proceeding.',
  },
  {
    id: 'name-verified',
    field: 'isNameVerified',
    label: 'Verify student name',
    description: 'Confirm the full name against valid identification.',
  },
  {
    id: 'payment-agreement',
    field: 'isPaymentAgreementGiven',
    label: 'Collect payment agreement',
    description: 'Ensure the payment agreement form is signed and stored.',
  },
  {
    id: 'student-declaration',
    field: 'isStudentDeclarationGiven',
    label: 'Collect student declaration',
    description: 'File the student declaration acknowledgment for records.',
  },
  {
    id: 'whatsapp-group',
    field: 'isStudentAddedInWhatsAppGroup',
    label: 'Add to WhatsApp group',
    description: 'Add the student to the relevant course WhatsApp group.',
  },
];

const CHECKLIST_FIELD_BY_TASK_ID = CHECKLIST_ITEMS.reduce((acc, item) => {
  acc[item.id] = item.field;
  return acc;
}, {});

const CHECKLIST_FIELDS = CHECKLIST_ITEMS.map((item) => item.field);

const UNKNOWN_COURSE_LABEL = 'Unknown Course';

const buildCourseFilterKey = (courseId, courseName) => {
  if (courseId !== null && courseId !== undefined && courseId !== '') {
    return `id:${courseId}`;
  }
  const normalized = (courseName || '').trim().toLowerCase();
  return normalized ? `name:${normalized}` : 'unknown';
};

const buildInquiryTypeKey = (value) => {
  const normalized = (value || '').trim().toLowerCase();
  return normalized || 'unknown';
};

const formatInquiryTypeLabel = (value) => {
  if (!value) return 'Unknown Type';
  return value
    .replace(/_/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const isChecklistComplete = (inquiry) => CHECKLIST_FIELDS.every((field) => Boolean(inquiry?.[field]));

export default function AdminSalesInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCounselor, setSelectedCounselor] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedInquiryType, setSelectedInquiryType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [registeredFilter, setRegisteredFilter] = useState({ enabled: false, showIncompleteOnly: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [checklistModal, setChecklistModal] = useState({ isOpen: false, inquiry: null, tasks: [] });
  const [followUpModal, setFollowUpModal] = useState({ isOpen: false, inquiry: null });
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [followUpForm, setFollowUpForm] = useState({
    comment: '',
    nextReminderDate: '',
    nextReminderComment: '',
    submitting: false,
    error: '',
  });
  const [counselorModal, setCounselorModal] = useState({ isOpen: false, inquiry: null, selectedStaffId: null, submitting: false, searchQuery: '' });
  const [staffList, setStaffList] = useState([]);
  const selectMenuPortalTarget = typeof window === 'undefined' ? undefined : document.body;

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [inquiriesRes, coursesRes, staffRes] = await Promise.all([
          axiosInstance.get('/admin/sales-dashboard/inquiries'),
          axiosInstance.get('/admin/sales-inquiries').catch(() => ({ data: { courseOptions: [] } })),
          axiosInstance.get('/admin/staff'),
        ]);

        if (ignore) return;

        setInquiries(Array.isArray(inquiriesRes.data) ? inquiriesRes.data : []);
        setCourseOptions(coursesRes.data?.courseOptions ?? []);
        
        // Handle staff data - backend returns { staff: [...], totalStaff, positions }
        let staffData = [];
        if (staffRes.data) {
          if (Array.isArray(staffRes.data)) {
            staffData = staffRes.data;
          } else if (Array.isArray(staffRes.data.staff)) {
            staffData = staffRes.data.staff;
          }
        }
        console.log('Staff data loaded:', staffData.length, 'staff members');
        setStaffList(staffData);
      } catch (err) {
        console.error('Error fetching inquiries:', err);
        console.error('Error details:', err.response?.data || err.message);
        if (!ignore) {
          setError('Unable to load inquiries right now.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, []);

  const courseLabelLookup = useMemo(() => {
    const map = new Map();
    courseOptions.forEach((option) => {
      const labelParts = [option.label, option.shortName, option.fullName].filter(Boolean);
      const derivedLabel = labelParts[0] || `Course ${option.value ?? option.id ?? ''}`;
      [option.value, option.id].forEach((key) => {
        if (key !== null && key !== undefined && key !== '') {
          map.set(String(key), derivedLabel);
        }
      });
    });
    return map;
  }, [courseOptions]);

  // Extract unique counselors from inquiries with counts
  const counselors = useMemo(() => {
    const counselorMap = new Map();
    inquiries.forEach((inquiry) => {
      // Prioritize assignedStaff, fallback to createdByStaff
      const staff = inquiry.assignedStaff || inquiry.createdByStaff;
      if (staff && staff.id) {
        if (!counselorMap.has(staff.id)) {
          counselorMap.set(staff.id, {
            staffId: staff.id,
            name: staff.name,
            imagePath: staff.imagePath,
            count: 0,
          });
        }
        const counselor = counselorMap.get(staff.id);
        counselor.count += 1;
      }
    });
    return Array.from(counselorMap.values()).sort((a, b) => 
      (a.name || '').localeCompare(b.name || '')
    );
  }, [inquiries]);

  const counselorOptions = useMemo(() => {
    const options = [
      {
        value: 'all',
        label: `All Counselors (${inquiries.length})`,
      },
    ];

    counselors.forEach((counselor) => {
      options.push({
        value: String(counselor.staffId),
        label: `${counselor.name || 'Unknown'} (${counselor.count})`,
      });
    });

    return options;
  }, [counselors, inquiries.length]);

  const selectedCounselorOption = useMemo(() => {
    return counselorOptions.find((option) => option.value === selectedCounselor) || counselorOptions[0];
  }, [counselorOptions, selectedCounselor]);

  useEffect(() => {
    if (!counselorOptions.some((option) => option.value === selectedCounselor)) {
      setSelectedCounselor('all');
    }
  }, [counselorOptions, selectedCounselor]);

  const courseFilterOptions = useMemo(() => {
    const counts = new Map();

    inquiries.forEach((inquiry) => {
      const key = buildCourseFilterKey(inquiry.courseId ?? inquiry.course_id ?? null, inquiry.course ?? inquiry.courseName ?? null);
      const courseIdKey = inquiry.courseId ?? inquiry.course_id ?? null;
      const lookupLabel = courseIdKey !== null && courseIdKey !== undefined
        ? courseLabelLookup.get(String(courseIdKey))
        : null;
      const courseName = lookupLabel || (inquiry.course?.trim() || UNKNOWN_COURSE_LABEL);

      if (!counts.has(key)) {
        counts.set(key, { label: courseName, count: 0 });
      }
      const entry = counts.get(key);
      entry.label = lookupLabel || entry.label || courseName;
      entry.count += 1;
    });

    const sorted = Array.from(counts.entries())
      .sort((a, b) => a[1].label.localeCompare(b[1].label))
      .map(([value, data]) => ({ value, label: `${data.label} (${data.count})` }));

    return [
      { value: 'all', label: `All Courses (${inquiries.length})` },
      ...sorted,
    ];
  }, [courseLabelLookup, inquiries]);

  const selectedCourseOption = useMemo(() => {
    return courseFilterOptions.find((option) => option.value === selectedCourse) || courseFilterOptions[0];
  }, [courseFilterOptions, selectedCourse]);

  useEffect(() => {
    if (!courseFilterOptions.some((option) => option.value === selectedCourse)) {
      setSelectedCourse('all');
    }
  }, [courseFilterOptions, selectedCourse]);

  const inquiryTypeFilterOptions = useMemo(() => {
    const counts = new Map();

    inquiries.forEach((inquiry) => {
      const key = buildInquiryTypeKey(inquiry.inquiryType);
      const label = key === 'unknown'
        ? 'Unknown Type'
        : formatInquiryTypeLabel(inquiry.inquiryType || '');

      if (!counts.has(key)) {
        counts.set(key, { label, count: 0 });
      }
      counts.get(key).count += 1;
    });

    const sorted = Array.from(counts.entries())
      .sort((a, b) => a[1].label.localeCompare(b[1].label))
      .map(([value, data]) => ({ value, label: `${data.label} (${data.count})` }));

    return [
      { value: 'all', label: `All Inquiry Types (${inquiries.length})` },
      ...sorted,
    ];
  }, [inquiries]);

  const selectedInquiryTypeOption = useMemo(() => {
    return inquiryTypeFilterOptions.find((option) => option.value === selectedInquiryType) || inquiryTypeFilterOptions[0];
  }, [inquiryTypeFilterOptions, selectedInquiryType]);

  useEffect(() => {
    if (!inquiryTypeFilterOptions.some((option) => option.value === selectedInquiryType)) {
      setSelectedInquiryType('all');
    }
  }, [inquiryTypeFilterOptions, selectedInquiryType]);

  const statusFilterOptions = useMemo(() => {
    const counts = new Map();

    inquiries.forEach((inquiry) => {
      const key = normalizeStatusKey(inquiry.statusKey) || 'unknown';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    const sorted = Array.from(counts.entries())
      .sort((a, b) => formatStatusLabel(a[0]).localeCompare(formatStatusLabel(b[0])))
      .map(([value, count]) => ({ value, label: `${formatStatusLabel(value)} (${count})` }));

    return [
      { value: 'all', label: `All Statuses (${inquiries.length})` },
      ...sorted,
    ];
  }, [inquiries]);

  const selectedStatusOption = useMemo(() => {
    return statusFilterOptions.find((option) => option.value === selectedStatus) || statusFilterOptions[0];
  }, [statusFilterOptions, selectedStatus]);

  useEffect(() => {
    if (!statusFilterOptions.some((option) => option.value === selectedStatus)) {
      setSelectedStatus('all');
    }
  }, [statusFilterOptions, selectedStatus]);

  const openCounselorModal = (inquiry) => {
    // Prioritize assignedStaff, fallback to createdByStaff
    const counselorData = inquiry.assignedStaff || inquiry.createdByStaff;
    const currentCounselorId = counselorData?.id ?? null;
    setCounselorModal({ 
      isOpen: true, 
      inquiry, 
      selectedStaffId: currentCounselorId, 
      submitting: false,
      searchQuery: ''
    });
  };

  const closeCounselorModal = () => {
    setCounselorModal({ isOpen: false, inquiry: null, selectedStaffId: null, submitting: false, searchQuery: '' });
  };

  const submitCounselorChange = async () => {
    if (!counselorModal.inquiry || !counselorModal.selectedStaffId) return;

    const inquiryId = counselorModal.inquiry.id;
    const newStaffId = counselorModal.selectedStaffId;

    setCounselorModal(prev => ({ ...prev, submitting: true }));

    try {
      const response = await axiosInstance.put(`/admin/sales-inquiries/${inquiryId}`, {
        field: 'assignTo',
        value: String(newStaffId)
      });

      const updatedInquiry = response?.data?.data;
      if (updatedInquiry) {
        setInquiries(items => items.map(item => 
          item.id === updatedInquiry.id ? updatedInquiry : item
        ));
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Counselor updated successfully',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

      closeCounselorModal();
    } catch (error) {
      console.error('Error updating counselor:', error);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: 'Failed to update counselor',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
      setCounselorModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const openChecklistModal = (inquiry) => {
    const tasks = CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
      description: item.description,
      field: item.field,
      completed: Boolean(inquiry?.[item.field] ?? false),
    }));
    setChecklistModal({ isOpen: true, inquiry, tasks });
  };

  const closeChecklistModal = () => {
    setChecklistModal({ isOpen: false, inquiry: null, tasks: [] });
  };

  const toggleChecklistTask = (taskId) => {
    const field = CHECKLIST_FIELD_BY_TASK_ID[taskId];
    const inquiry = checklistModal.inquiry;

    if (!field || !inquiry) return;

    const inquiryId = inquiry.id;
    const nextValue = !Boolean(inquiry[field]);

    // Optimistic update
    setChecklistModal((prev) => ({
      ...prev,
      inquiry: prev.inquiry && prev.inquiry.id === inquiryId ? { ...prev.inquiry, [field]: nextValue } : prev.inquiry,
      tasks: prev.tasks.map((task) =>
        task.id === taskId ? { ...task, completed: nextValue } : task
      ),
    }));

    setInquiries((items) =>
      items.map((item) => (item.id === inquiryId ? { ...item, [field]: nextValue } : item))
    );

    // API call
    const payload = { field, value: nextValue ? '1' : '0' };
    axiosInstance
      .put(`/admin/sales-inquiries/${inquiryId}`, payload)
      .then(() => {
        // Success - optimistic update already applied
      })
      .catch(() => {
        // Revert on error
        setChecklistModal((prev) => ({
          ...prev,
          inquiry: prev.inquiry && prev.inquiry.id === inquiryId ? { ...prev.inquiry, [field]: !nextValue } : prev.inquiry,
          tasks: prev.tasks.map((task) =>
            task.id === taskId ? { ...task, completed: !nextValue } : task
          ),
        }));
        setInquiries((items) =>
          items.map((item) => (item.id === inquiryId ? { ...item, [field]: !nextValue } : item))
        );
      });
  };

  const openFollowUpModal = (inquiry) => {
    setFollowUpModal({ isOpen: true, inquiry });
    // Load follow-up history
    if (inquiry?.id) {
      axiosInstance
        .get(`/admin/sales-inquiries/${inquiry.id}/followups`)
        .then((res) => {
          const items = res.data?.data ?? [];
          setFollowUpHistory(items);
        })
        .catch(() => setFollowUpHistory([]));
    }
  };

  const closeFollowUpModal = () => {
    setFollowUpModal({ isOpen: false, inquiry: null });
    setFollowUpHistory([]);
    setFollowUpForm({
      comment: '',
      nextReminderDate: '',
      nextReminderComment: '',
      submitting: false,
      error: '',
    });
  };

  const submitFollowUp = () => {
    if (!followUpModal.inquiry) return;

    const payload = {
      comment: followUpForm.comment.trim(),
      next_reminder_date: followUpForm.nextReminderDate.trim() || null,
      next_reminder_comment: followUpForm.nextReminderComment.trim() || null,
    };

    if (!payload.comment) {
      setFollowUpForm((p) => ({ ...p, error: 'Comment is required.' }));
      return;
    }

    setFollowUpForm((p) => ({ ...p, submitting: true, error: '' }));
    axiosInstance
      .post(`/admin/sales-inquiries/${followUpModal.inquiry.id}/followups`, payload)
      .then(() => {
        // Reload history
        return axiosInstance.get(`/admin/sales-inquiries/${followUpModal.inquiry.id}/followups`);
      })
      .then((res) => {
        const items = res.data?.data ?? [];
        setFollowUpHistory(items);
        setFollowUpForm({
          comment: '',
          nextReminderDate: '',
          nextReminderComment: '',
          submitting: false,
          error: '',
        });
      })
      .catch((err) => {
        setFollowUpForm((p) => ({
          ...p,
          submitting: false,
          error: err?.response?.data?.message || err.message || 'Unable to save follow-up.',
        }));
      });
  };

  const formatDisplayDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(parsed);
  };

  const beginEditing = (inquiryId, field) => {
    if (editingCell && (editingCell.inquiryId !== inquiryId || editingCell.field !== field)) {
      finalizeEdit(false);
    }

    const inquiry = inquiries.find((item) => item.id === inquiryId);
    if (!inquiry) return;

    let initialValue = '';
    if (field === 'agreedAmount') {
      initialValue = inquiry.agreedAmount !== null && inquiry.agreedAmount !== undefined ? String(inquiry.agreedAmount) : '';
    } else if (field === 'statusKey') {
      initialValue = inquiry.statusKey ?? '';
    } else if (field === 'course') {
      initialValue = inquiry.courseId !== null && inquiry.courseId !== undefined ? String(inquiry.courseId) : '';
    } else {
      const rawValue = inquiry[field];
      initialValue = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
    }

    setEditingCell({ inquiryId, field, value: initialValue });
  };

  const updateEditingValue = (value) => {
    setEditingCell((current) => (current ? { ...current, value } : current));
  };

  const finalizeEdit = (commit, overrideValue, metadata) => {
    if (!editingCell) return;

    if (!commit) {
      setEditingCell(null);
      return;
    }

    const { inquiryId, field, value } = editingCell;
    const nextValue = overrideValue !== undefined ? overrideValue : value;
    const existingInquiry = inquiries.find((item) => item.id === inquiryId);

    if (!existingInquiry) {
      setEditingCell(null);
      return;
    }

    const updatedInquiry = applyFieldUpdate(existingInquiry, field, nextValue, metadata);
    const previousValue = getFieldValue(existingInquiry, field);
    const nextValueNormalized = getFieldValue(updatedInquiry, field);

    if (String(previousValue ?? '') === String(nextValueNormalized ?? '')) {
      setEditingCell(null);
      return;
    }

    setInquiries((prev) => prev.map((item) => (item.id === inquiryId ? updatedInquiry : item)));
    persistInquiryField(field, updatedInquiry, existingInquiry, metadata);
    setEditingCell(null);
  };

  const applyFieldUpdate = (inquiry, field, rawValue, metadata) => {
    const value = rawValue ?? '';
    const trimmed = typeof value === 'string' ? value.trim() : String(value).trim();
    const updated = { ...inquiry };

    if (field === 'statusKey') {
      const normalized = normalizeStatusKey(trimmed) || inquiry.statusKey;
      updated.statusKey = normalized;
      updated.statusLabel = formatStatusLabel(normalized);
      return updated;
    }

    if (field === 'agreedAmount') {
      if (trimmed === '') {
        updated.agreedAmount = null;
        return updated;
      }
      const parsed = Number(trimmed);
      if (Number.isNaN(parsed)) return inquiry;
      updated.agreedAmount = parsed;
      return updated;
    }

    if (field === 'registerDate') {
      const normalizedDate = trimmed || null;
      updated.registerDate = normalizedDate;
      updated.confirmedDateToRegister = normalizedDate;
      return updated;
    }

    if (field === 'course') {
      const courseOption = metadata?.courseOption ?? null;
      if (courseOption) {
        const parsedId = Number.parseInt(courseOption.value, 10);
        updated.courseId = Number.isNaN(parsedId) ? null : parsedId;
        updated.course = courseOption.label;
        return updated;
      }
      if (trimmed === '') {
        updated.courseId = null;
        updated.course = null;
        return updated;
      }
      const numericId = /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : null;
      updated.courseId = numericId;
      updated.course = trimmed;
      return updated;
    }

    updated[field] = trimmed || null;
    if (field === 'contact') {
      updated.contactNumber1 = trimmed || null;
    }
    return updated;
  };

  const getFieldValue = (inquiry, field) => {
    switch (field) {
      case 'studentId': return inquiry.studentId ?? null;
      case 'studentName': return inquiry.studentName ?? null;
      case 'nic': return inquiry.nic ?? null;
      case 'contact': return inquiry.contact ?? null;
      case 'course': return inquiry.courseId ?? inquiry.course ?? null;
      case 'inquiryType': return inquiry.inquiryType ?? null;
      case 'note': return inquiry.note ?? null;
      case 'statusKey': return inquiry.statusKey;
      case 'agreedAmount': return inquiry.agreedAmount ?? null;
      case 'registerDate': return inquiry.registerDate ?? null;
      default: return null;
    }
  };

  const persistInquiryField = (field, updatedInquiry, previousInquiry, metadata) => {
    const payloadValue = metadata?.payloadValue !== undefined
      ? metadata.payloadValue
      : String(getFieldValue(updatedInquiry, field) ?? '');

    const payload = { field, value: payloadValue };

    axiosInstance
      .put(`/admin/sales-inquiries/${updatedInquiry.id}`, payload)
      .then((response) => {
        const serverInquiry = response?.data?.data ?? null;
        if (!serverInquiry) return;
        setInquiries((items) => items.map((item) => (item.id === serverInquiry.id ? serverInquiry : item)));
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Updated successfully',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
      })
      .catch(() => {
        setInquiries((items) => items.map((item) => (item.id === previousInquiry.id ? previousInquiry : item)));
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: 'Update failed',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
      });
  };

  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inquiry) => {
      const counselorData = inquiry.assignedStaff || inquiry.createdByStaff;
      const counselorId = counselorData?.id ? String(counselorData.id) : null;
      const matchesCounselor = selectedCounselor === 'all' || counselorId === selectedCounselor;

      const courseKey = buildCourseFilterKey(inquiry.courseId ?? inquiry.course_id ?? null, inquiry.course ?? inquiry.courseName ?? null);
      const matchesCourse = selectedCourse === 'all' || courseKey === selectedCourse;

      const inquiryTypeKey = buildInquiryTypeKey(inquiry.inquiryType);
      const matchesInquiryType = selectedInquiryType === 'all' || inquiryTypeKey === selectedInquiryType;

      const statusKey = normalizeStatusKey(inquiry.statusKey) || 'unknown';
      const matchesStatus = selectedStatus === 'all' || statusKey === selectedStatus;

      if (registeredFilter.enabled) {
        const isRegistered = statusKey.includes('registered');
        if (!isRegistered) return false;
        if (registeredFilter.showIncompleteOnly && isChecklistComplete(inquiry)) {
          return false;
        }
      }

      return matchesCounselor && matchesCourse && matchesInquiryType && matchesStatus;
    });
  }, [inquiries, selectedCounselor, selectedCourse, selectedInquiryType, selectedStatus, registeredFilter]);

  const courseSelectOptions = useMemo(() => {
    const seenLabels = new Map();
    return courseOptions
      .map((option) => {
        const shortName = option.shortName ?? null;
        const fullName = option.fullName ?? null;
        const derivedLabel = option.label || [shortName, fullName].filter(Boolean).join('  ');

        return {
          value: option.value,
          label: derivedLabel,
          shortName,
          fullName,
          searchValue: [derivedLabel, shortName, fullName]
            .filter((part) => Boolean(part))
            .map((part) => part.toLowerCase())
            .join(' '),
        };
      })
      .filter((option) => {
        const labelKey = option.label.toLowerCase().trim();
        if (seenLabels.has(labelKey)) {
          return false;
        }
        seenLabels.set(labelKey, true);
        return true;
      });
  }, [courseOptions]);

  const statusOptions = useMemo(() => {
    const map = new Map(STATUS_OPTIONS.map((option) => [option.value, option]));
    inquiries.forEach((inquiry) => {
      const key = normalizeStatusKey(inquiry.statusKey);
      if (!key || map.has(key)) return;
      map.set(key, { value: key, label: formatStatusLabel(key) });
    });
    return Array.from(map.values());
  }, [inquiries]);

  const inquiryTypeOptions = useMemo(() => {
    const map = new Map();
    inquiries.forEach((inquiry) => {
      const type = inquiry.inquiryType?.trim();
      if (!type) return;
      if (!map.has(type)) {
        map.set(type, { value: type, label: type });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [inquiries]);

  const stats = useMemo(() => {
    const total = filteredInquiries.length;
    const registered = filteredInquiries.filter((i) => 
      i.statusKey?.includes('registered')
    ).length;
    const inFollowup = filteredInquiries.filter((i) => 
      i.statusKey?.includes('followup')
    ).length;
    const planToRegister = filteredInquiries.filter((i) => 
      i.statusKey?.includes('plan')
    ).length;

    return { total, registered, inFollowup, planToRegister };
  }, [filteredInquiries]);

  const handleRegisteredFilterToggle = () => {
    setRegisteredFilter((prev) => {
      if (!prev.enabled) {
        return { enabled: true, showIncompleteOnly: false };
      }
      if (!prev.showIncompleteOnly) {
        return { enabled: true, showIncompleteOnly: true };
      }
      return { enabled: true, showIncompleteOnly: false };
    });
  };

  const clearRegisteredFilter = () => {
    setRegisteredFilter({ enabled: false, showIncompleteOnly: false });
  };

  const registeredToggleLabel = registeredFilter.enabled
    ? (registeredFilter.showIncompleteOnly ? 'Registered • Checklist Pending' : 'Registered • All')
    : 'Registered Filter';

  const registeredToggleHint = registeredFilter.enabled
    ? (registeredFilter.showIncompleteOnly
      ? 'Showing registered inquiries with incomplete checklist items.'
      : 'Showing every inquiry marked as registered.')
    : 'Click to focus on registered inquiries or cycle to pending checklists.';

  return (
    <AdminSalesLayout title="Student Inquiries" currentSection="inquiries">
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid #e5e7eb' }}>
          <Title style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Student Inquiries Overview
          </Title>
          <div style={{ display: 'flex', gap: '20px', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>
            <span>Total: {stats.total}</span>
            <span style={{ color: '#10b981' }}>Registered: {stats.registered}</span>
            <span style={{ color: '#3b82f6' }}>In Followup: {stats.inFollowup}</span>
            <span style={{ color: '#f59e0b' }}>Plan to Register: {stats.planToRegister}</span>
          </div>
        </div>

        <FilterSection>
          <FilterGroup>
            <FilterLabel>Course</FilterLabel>
            <Select
              instanceId="admin-sales-course-filter"
              options={courseFilterOptions}
              value={selectedCourseOption}
              onChange={(option) => setSelectedCourse(option?.value ?? 'all')}
              styles={filterSelectStyles}
              isSearchable
              menuPortalTarget={selectMenuPortalTarget}
              placeholder="All courses"
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Inquiry Type</FilterLabel>
            <Select
              instanceId="admin-sales-inquiry-type-filter"
              options={inquiryTypeFilterOptions}
              value={selectedInquiryTypeOption}
              onChange={(option) => setSelectedInquiryType(option?.value ?? 'all')}
              styles={filterSelectStyles}
              isSearchable
              menuPortalTarget={selectMenuPortalTarget}
              placeholder="All inquiry types"
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Counselor</FilterLabel>
            <Select
              instanceId="admin-sales-counselor-filter"
              options={counselorOptions}
              value={selectedCounselorOption}
              onChange={(option) => setSelectedCounselor(option?.value ?? 'all')}
              styles={filterSelectStyles}
              isSearchable
              menuPortalTarget={selectMenuPortalTarget}
              placeholder="All counselors"
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <Select
              instanceId="admin-sales-status-filter"
              options={statusFilterOptions}
              value={selectedStatusOption}
              onChange={(option) => setSelectedStatus(option?.value ?? 'all')}
              styles={filterSelectStyles}
              isSearchable
              menuPortalTarget={selectMenuPortalTarget}
              placeholder="All statuses"
            />
          </FilterGroup>
        </FilterSection>

        <RegisteredToggleRow>
          <RegisteredToggleButton
            type="button"
            onClick={handleRegisteredFilterToggle}
            $active={registeredFilter.enabled}
            $pending={registeredFilter.showIncompleteOnly}
          >
            {registeredToggleLabel}
          </RegisteredToggleButton>
          {registeredFilter.enabled && (
            <ClearToggleButton type="button" onClick={clearRegisteredFilter}>
              Clear
            </ClearToggleButton>
          )}
          <RegisteredToggleHint>{registeredToggleHint}</RegisteredToggleHint>
        </RegisteredToggleRow>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>Loading inquiries...</p>
        ) : error ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#ef4444' }}>{error}</p>
        ) : filteredInquiries.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No inquiries found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th style={{ minWidth: '100px' }}>Student ID</Th>
                  <Th style={{ minWidth: '150px' }}>Student Name</Th>
                  <Th style={{ minWidth: '120px' }}>NIC</Th>
                  <Th style={{ minWidth: '120px' }}>Contact</Th>
                  <Th style={{ minWidth: '200px', maxWidth: '300px' }}>Course</Th>
                  <Th style={{ minWidth: '200px' }}>Inquiry Type</Th>
                  <Th style={{ minWidth: '150px' }}>Counselor</Th>
                  <Th style={{ minWidth: '150px' }}>Note</Th>
                  <Th style={{ minWidth: '120px' }}>Status</Th>
                  <Th style={{ textAlign: 'right', minWidth: '130px' }}>Agreed Amount</Th>
                  <Th style={{ minWidth: '120px' }}>Register Date</Th>
                  <Th style={{ minWidth: '140px' }}>Checklist</Th>
                  <Th style={{ minWidth: '220px' }}>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filteredInquiries.map((inquiry) => {
                  // Prioritize assignedStaff, fallback to createdByStaff
                  const counselorData = inquiry.assignedStaff || inquiry.createdByStaff;
                  const counselorName = counselorData?.name || inquiry.createdBy || '—';
                  const imagePath = counselorData?.imagePath;
                  const counselorImageSrc = buildStaffImageSrc(imagePath);
                  const counselorInitials = getInitials(counselorName);
                  const isRegistered = inquiry.statusKey === 'registered';
                  
                  // Checklist calculation
                  const checklistFields = [
                    inquiry.isPhotoUploaded,
                    inquiry.isNameVerified,
                    inquiry.isPaymentAgreementGiven,
                    inquiry.isStudentDeclarationGiven,
                    inquiry.isStudentAddedInWhatsAppGroup,
                  ];
                  const checklistCompleted = checklistFields.filter(Boolean).length;
                  const checklistTotal = 5;
                  const checklistPercentage = Math.round((checklistCompleted / checklistTotal) * 100);

                  return (
                    <tr key={inquiry.id}>
                      <Td
                        style={{ fontWeight: 600, color: inquiry.studentId ? '#111827' : '#9ca3af', cursor: 'text', minWidth: '100px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'studentId')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'studentId' ? (
                          <input
                            autoFocus
                            type="text"
                            value={editingCell.value}
                            onChange={(e) => updateEditingValue(e.target.value)}
                            onBlur={() => finalizeEdit(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') finalizeEdit(true);
                              else if (e.key === 'Escape') finalizeEdit(false);
                            }}
                            style={{
                              width: '100%',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                            }}
                          />
                        ) : (
                          inquiry.studentId || '—'
                        )}
                      </Td>
                      <Td
                        style={{ fontWeight: 600, color: '#111827', cursor: 'text', minWidth: '150px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'studentName')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'studentName' ? (
                          <input
                            autoFocus
                            type="text"
                            value={editingCell.value}
                            onChange={(e) => updateEditingValue(e.target.value)}
                            onBlur={() => finalizeEdit(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') finalizeEdit(true);
                              else if (e.key === 'Escape') finalizeEdit(false);
                            }}
                            style={{
                              width: '100%',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                            }}
                          />
                        ) : (
                          inquiry.studentName || '—'
                        )}
                      </Td>
                      <Td
                        style={{ color: '#6b7280', fontSize: '12px', fontFamily: 'monospace', textTransform: 'uppercase', cursor: 'text', minWidth: '120px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'nic')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'nic' ? (
                          <input
                            autoFocus
                            type="text"
                            value={editingCell.value}
                            onChange={(e) => updateEditingValue(e.target.value)}
                            onBlur={() => finalizeEdit(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') finalizeEdit(true);
                              else if (e.key === 'Escape') finalizeEdit(false);
                            }}
                            style={{
                              width: '100%',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              textTransform: 'uppercase',
                            }}
                          />
                        ) : (
                          inquiry.nic || '—'
                        )}
                      </Td>
                      <Td
                        style={{ color: '#6b7280', fontSize: '13px', cursor: 'text', minWidth: '120px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'contact')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'contact' ? (
                          <input
                            autoFocus
                            type="text"
                            value={editingCell.value}
                            onChange={(e) => updateEditingValue(e.target.value)}
                            onBlur={() => finalizeEdit(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') finalizeEdit(true);
                              else if (e.key === 'Escape') finalizeEdit(false);
                            }}
                            style={{
                              width: '100%',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                            }}
                          />
                        ) : (
                          inquiry.contact || inquiry.contactNumber1 || '—'
                        )}
                      </Td>
                      <Td
                        style={{ minWidth: '200px', maxWidth: '300px', cursor: 'text' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'course')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'course' ? (
                          <Select
                            autoFocus
                            styles={selectStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            options={courseSelectOptions}
                            value={courseSelectOptions.find((opt) => opt.value === editingCell.value) ?? null}
                            onChange={(option) => {
                              finalizeEdit(true, option?.value ?? '', {
                                courseOption: option ?? null,
                                payloadValue: option ? option.value : '',
                              });
                            }}
                            onBlur={() => finalizeEdit(false)}
                            isClearable
                            placeholder="Select course"
                          />
                        ) : (
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={inquiry.course || ''}>
                            {inquiry.course || '—'}
                          </div>
                        )}
                      </Td>
                      <Td
                        style={{ color: '#6b7280', cursor: 'text', minWidth: '120px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'inquiryType')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'inquiryType' ? (
                          inquiryTypeOptions.length > 0 ? (
                            <Select
                              autoFocus
                              styles={selectStyles}
                              menuPortalTarget={selectMenuPortalTarget}
                              menuPosition="fixed"
                              options={inquiryTypeOptions}
                              value={inquiryTypeOptions.find((opt) => opt.value === editingCell.value) ?? null}
                              onChange={(option) => finalizeEdit(true, option?.value ?? '')}
                              onBlur={() => finalizeEdit(true)}
                              isClearable
                              placeholder="Select type"
                            />
                          ) : (
                            <input
                              autoFocus
                              type="text"
                              value={editingCell.value}
                              onChange={(e) => updateEditingValue(e.target.value)}
                              onBlur={() => finalizeEdit(true)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') finalizeEdit(true);
                                else if (e.key === 'Escape') finalizeEdit(false);
                              }}
                              style={{
                                width: '100%',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '12px',
                              }}
                            />
                          )
                        ) : (
                          inquiry.inquiryType || '—'
                        )}
                      </Td>
                      <Td 
                        style={{ minWidth: '150px', cursor: 'pointer' }}
                        onDoubleClick={() => openCounselorModal(inquiry)}
                        title="Double-click to reassign counselor"
                      >
                        <CounselorInfo>
                          {counselorImageSrc ? (
                            <Avatar src={counselorImageSrc} alt={counselorName} />
                          ) : (
                            <AvatarPlaceholder>{counselorInitials}</AvatarPlaceholder>
                          )}
                          <span style={{ fontSize: '13px', fontWeight: 500 }}>
                            {counselorName}
                          </span>
                        </CounselorInfo>
                      </Td>
                      <Td
                        style={{ minWidth: '150px', maxWidth: '250px', color: '#6b7280', fontSize: '13px', cursor: 'text' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'note')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'note' ? (
                          <textarea
                            autoFocus
                            value={editingCell.value}
                            onChange={(e) => updateEditingValue(e.target.value)}
                            onBlur={() => finalizeEdit(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                finalizeEdit(true);
                              } else if (e.key === 'Escape') {
                                finalizeEdit(false);
                              }
                            }}
                            rows={2}
                            style={{
                              width: '100%',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              resize: 'vertical',
                            }}
                          />
                        ) : (
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={inquiry.note || ''}>
                            {inquiry.note || '—'}
                          </div>
                        )}
                      </Td>
                      <Td
                        style={{ cursor: 'pointer', minWidth: '120px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'statusKey')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'statusKey' ? (
                          <Select
                            autoFocus
                            styles={selectStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            options={statusOptions}
                            value={statusOptions.find((opt) => opt.value === editingCell.value) ?? null}
                            onChange={(option) => finalizeEdit(true, option?.value ?? inquiry.statusKey)}
                            onBlur={() => finalizeEdit(true)}
                            placeholder="Select status"
                          />
                        ) : (
                          <StatusBadge $status={inquiry.statusKey}>
                            {formatStatusLabel(inquiry.statusKey)}
                          </StatusBadge>
                        )}
                      </Td>
                      <Td
                        style={{ textAlign: 'right', fontWeight: 600, color: inquiry.agreedAmount ? '#10b981' : '#6b7280', cursor: 'text', minWidth: '130px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'agreedAmount')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'agreedAmount' ? (
                          <input
                            autoFocus
                            type="number"
                            step="0.01"
                            value={editingCell.value}
                            onChange={(e) => updateEditingValue(e.target.value)}
                            onBlur={() => finalizeEdit(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') finalizeEdit(true);
                              else if (e.key === 'Escape') finalizeEdit(false);
                            }}
                            style={{
                              width: '100%',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              textAlign: 'right',
                            }}
                          />
                        ) : (
                          inquiry.agreedAmount ? formatCurrency(inquiry.agreedAmount) : '—'
                        )}
                      </Td>
                      <Td
                        style={{ color: '#6b7280', fontSize: '13px', cursor: 'text', minWidth: '120px' }}
                        onDoubleClick={() => beginEditing(inquiry.id, 'registerDate')}
                      >
                        {editingCell?.inquiryId === inquiry.id && editingCell.field === 'registerDate' ? (
                          <input
                            autoFocus
                            type="date"
                            value={editingCell.value}
                            onChange={(e) => updateEditingValue(e.target.value)}
                            onBlur={() => finalizeEdit(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') finalizeEdit(true);
                              else if (e.key === 'Escape') finalizeEdit(false);
                            }}
                            style={{
                              width: '100%',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                            }}
                          />
                        ) : (
                          inquiry.registerDate ? new Date(inquiry.registerDate).toLocaleDateString('en-GB') : '—'
                        )}
                      </Td>
                      <Td style={{ minWidth: '140px' }}>
                        {isRegistered ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', fontWeight: 600, color: '#374151' }}>
                              <span>{checklistPercentage}% complete</span>
                              <span style={{ fontSize: '10px', fontWeight: 500, color: '#6b7280' }}>
                                {checklistCompleted}/{checklistTotal}
                              </span>
                            </div>
                            <div style={{ height: '6px', width: '100%', borderRadius: '3px', background: '#e5e7eb', overflow: 'hidden' }}>
                              <div
                                style={{
                                  height: '100%',
                                  width: `${checklistPercentage}%`,
                                  background: checklistPercentage === 100 ? '#10b981' : '#3b82f6',
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>—</span>
                        )}
                      </Td>
                      <Td style={{ minWidth: '220px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {isRegistered && (
                            <button
                              type="button"
                              style={{
                                padding: '6px 12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: 'white',
                                background: '#10b981',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
                              onClick={() => openChecklistModal(inquiry)}
                            >
                              Checklist
                            </button>
                          )}
                          {!isRegistered && inquiry.statusKey !== 'pending' && (
                            <button
                              type="button"
                              style={{
                                padding: '6px 12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: 'white',
                                background: '#3b82f6',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                              onClick={() => openFollowUpModal(inquiry)}
                            >
                              Follow Up
                            </button>
                          )}
                          <button
                            type="button"
                            style={{
                              padding: '6px 12px',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: 'white',
                              background: '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'background 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#1f2937'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#374151'}
                            onClick={() => {
                              // TODO: Implement view details modal
                              alert('View details functionality coming soon!');
                            }}
                          >
                            View
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      {/* Checklist Modal - Right to Left */}
      {checklistModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(17, 24, 39, 0.6)',
            }}
            onClick={closeChecklistModal}
          />
          <div
            style={{
              position: 'relative',
              marginLeft: 'auto',
              height: '100%',
              width: '100%',
              maxWidth: '28rem',
              background: 'white',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transform: 'translateX(0)',
              transition: 'transform 0.3s ease-out',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', padding: '12px 16px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                  Registration Checklist
                </h3>
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                  Check off the steps required to complete this registration.
                </p>
              </div>
              <button
                type="button"
                onClick={closeChecklistModal}
                style={{
                  borderRadius: '9999px',
                  padding: '4px',
                  color: '#6b7280',
                  transition: 'all 0.2s',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.348 5.652a.75.75 0 0 0-1.06-1.06L10 7.879 6.712 4.592a.75.75 0 0 0-1.06 1.06L8.94 9l-3.287 3.288a.75.75 0 1 0 1.06 1.06L10 10.121l3.288 3.287a.75.75 0 1 0 1.06-1.06L11.061 9l3.287-3.348Z" fill="currentColor" />
                </svg>
              </button>
            </div>

            <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {checklistModal.inquiry?.studentName && (
                  <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb', padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                    <span style={{ fontWeight: 600, color: '#374151' }}>Student:</span> {checklistModal.inquiry.studentName}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {checklistModal.tasks.map((task) => (
                    <label
                      key={task.id}
                      style={{
                        display: 'flex',
                        gap: '12px',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: 'white',
                        padding: '12px',
                        fontSize: '14px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#bfdbfe';
                        e.currentTarget.style.background = '#eff6ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{
                          marginTop: '4px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          accentColor: '#3b82f6',
                          cursor: 'pointer',
                        }}
                        checked={task.completed}
                        onChange={() => toggleChecklistTask(task.id)}
                      />
                      <span>
                        <span style={{ fontWeight: 600, color: '#1f2937' }}>{task.label}</span>
                        {task.description && (
                          <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                            {task.description}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>

                <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                  <p style={{ fontWeight: 600, color: '#374151' }}>Need another step?</p>
                  <p style={{ marginTop: '4px' }}>
                    Use the follow-up panel to log custom actions or reminders for this inquiry.
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px' }}>
                  <button
                    type="button"
                    onClick={closeChecklistModal}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#4b5563',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow Up Modal - Right to Left */}
      {followUpModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(17, 24, 39, 0.6)',
            }}
            onClick={closeFollowUpModal}
          />
          <div
            style={{
              position: 'relative',
              marginLeft: 'auto',
              height: '100%',
              width: '100%',
              maxWidth: '28rem',
              background: 'white',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transform: 'translateX(0)',
              transition: 'transform 0.3s ease-out',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', padding: '12px 16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Follow Up</h3>
              <button
                type="button"
                onClick={closeFollowUpModal}
                style={{
                  borderRadius: '9999px',
                  padding: '4px',
                  color: '#6b7280',
                  transition: 'all 0.2s',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.348 5.652a.75.75 0 0 0-1.06-1.06L10 7.879 6.712 4.592a.75.75 0 0 0-1.06 1.06L8.94 9l-3.287 3.288a.75.75 0 1 0 1.06 1.06L10 10.121l3.288 3.287a.75.75 0 1 0 1.06-1.06L11.061 9l3.287-3.348Z" fill="currentColor" />
                </svg>
              </button>
            </div>

            <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {followUpModal.inquiry?.studentName ? (
                    <span>
                      Plan next steps for <strong style={{ fontWeight: 600, color: '#374151' }}>{followUpModal.inquiry.studentName}</strong>.
                    </span>
                  ) : (
                    <span>Plan your next follow-up.</span>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                    Add comment
                  </label>
                  <textarea
                    value={followUpForm.comment}
                    onChange={(e) => setFollowUpForm((p) => ({ ...p, comment: e.target.value }))}
                    style={{
                      marginTop: '4px',
                      width: '100%',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#374151',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      resize: 'vertical',
                    }}
                    rows={4}
                    placeholder="Add follow-up comment..."
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                    Next reminder date
                  </label>
                  <input
                    type="date"
                    value={followUpForm.nextReminderDate}
                    onChange={(e) => setFollowUpForm((p) => ({ ...p, nextReminderDate: e.target.value }))}
                    style={{
                      marginTop: '4px',
                      width: '100%',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#374151',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                    Next reminder comment
                  </label>
                  <textarea
                    value={followUpForm.nextReminderComment}
                    onChange={(e) => setFollowUpForm((p) => ({ ...p, nextReminderComment: e.target.value }))}
                    style={{
                      marginTop: '4px',
                      width: '100%',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#374151',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      resize: 'vertical',
                    }}
                    rows={2}
                    placeholder="Optional note for next reminder..."
                  />
                </div>

                {followUpForm.error && (
                  <div style={{ borderRadius: '6px', border: '1px solid #fecaca', background: '#fef2f2', padding: '8px 12px', fontSize: '12px', color: '#dc2626' }}>
                    {followUpForm.error}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px' }}>
                  <button
                    type="button"
                    onClick={closeFollowUpModal}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#4b5563',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={submitFollowUp}
                    disabled={followUpForm.submitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      border: 'none',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'white',
                      background: followUpForm.submitting ? '#93c5fd' : '#3b82f6',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s',
                      cursor: followUpForm.submitting ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!followUpForm.submitting) e.currentTarget.style.background = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      if (!followUpForm.submitting) e.currentTarget.style.background = '#3b82f6';
                    }}
                  >
                    {followUpForm.submitting ? 'Saving...' : 'Save Follow Up'}
                  </button>
                </div>

                {/* History */}
                <div style={{ paddingTop: '8px' }}>
                  <h4 style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                    Follow-up History
                  </h4>
                  <div style={{ marginTop: '8px', maxHeight: '16rem', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    {followUpHistory.length === 0 ? (
                      <div style={{ padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                        No previous follow-ups yet.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid #f3f4f6' }}>
                        {followUpHistory.map((item) => {
                          const staffName = item.createdByStaff?.name || item.createdBy || '—';
                          const staffAvatarSrc = buildStaffImageSrc(item.createdByStaff?.imagePath);
                          const staffInitials = getInitials(staffName);

                          return (
                            <div key={item.id} style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {staffAvatarSrc ? (
                                    <Avatar src={staffAvatarSrc} alt={staffName} />
                                  ) : (
                                    <AvatarPlaceholder>{staffInitials}</AvatarPlaceholder>
                                  )}
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#111827' }}>
                                      {staffName}
                                    </span>
                                  </div>
                                </div>
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                  {formatDisplayDate(item.createdDate || item.commentDate)}
                                </span>
                              </div>
                              {item.comment && (
                                <p style={{ marginTop: '4px', fontSize: '12px', color: '#374151' }}>{item.comment}</p>
                              )}
                              {(item.nextReminderDate || item.nextReminderComment) && (
                                <div style={{ marginTop: '8px', borderRadius: '6px', background: '#f9fafb', padding: '8px', fontSize: '11px', color: '#6b7280' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                    <span style={{ fontWeight: 600, color: '#374151' }}>Next follow-up</span>
                                    <span style={{ fontWeight: 500, color: '#1f2937' }}>
                                      {formatDisplayDate(item.nextReminderDate) || '—'}
                                    </span>
                                  </div>
                                  {item.nextReminderComment && (
                                    <p style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                                      {item.nextReminderComment}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Counselor Modal - Centered */}
      {counselorModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(17, 24, 39, 0.6)',
            }}
            onClick={closeCounselorModal}
          />
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'modalFadeIn 0.2s ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', padding: '20px 24px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                  Reassign Counselor
                </h3>
                <p style={{ marginTop: '4px', fontSize: '13px', color: '#6b7280' }}>
                  Select a new counselor for this inquiry
                </p>
              </div>
              <button
                type="button"
                onClick={closeCounselorModal}
                style={{
                  borderRadius: '9999px',
                  padding: '4px',
                  color: '#6b7280',
                  transition: 'all 0.2s',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.348 5.652a.75.75 0 0 0-1.06-1.06L10 7.879 6.712 4.592a.75.75 0 0 0-1.06 1.06L8.94 9l-3.287 3.288a.75.75 0 1 0 1.06 1.06L10 10.121l3.288 3.287a.75.75 0 1 0 1.06-1.06L11.061 9l3.287-3.348Z" fill="currentColor" />
                </svg>
              </button>
            </div>

            <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {counselorModal.inquiry?.studentName && (
                  <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb', padding: '14px', fontSize: '13px', color: '#6b7280' }}>
                    <span style={{ fontWeight: 600, color: '#374151' }}>Student:</span> {counselorModal.inquiry.studentName}
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280', marginBottom: '8px' }}>
                    Select Active Counselor
                  </label>
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={counselorModal.searchQuery}
                    onChange={(e) => setCounselorModal(prev => ({ ...prev, searchQuery: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      marginBottom: '12px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                  />
                  {staffList.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                      No staff members available
                    </div>
                  ) : (() => {
                    const activeStaff = staffList.filter(staff => staff.loginStatus !== 'inactive' && staff.loginStatus !== 'deleted');
                    const filteredStaff = activeStaff.filter(staff => 
                      staff.name?.toLowerCase().includes(counselorModal.searchQuery.toLowerCase())
                    );
                    
                    if (filteredStaff.length === 0) {
                      return (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                          No staff found matching "{counselorModal.searchQuery}"
                        </div>
                      );
                    }

                    return (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                          Showing {filteredStaff.length} of {activeStaff.length} staff members
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px', maxHeight: '400px', overflowY: 'auto', padding: '4px' }}>
                          {filteredStaff.map((staff) => {
                            const isSelected = counselorModal.selectedStaffId === staff.staffId;
                            const staffImageSrc = buildStaffImageSrc(staff.imagePath);
                            const staffInitials = getInitials(staff.name);

                            return (
                        <div
                          key={staff.staffId}
                          onClick={() => setCounselorModal(prev => ({ ...prev, selectedStaffId: staff.staffId }))}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            borderRadius: '10px',
                            border: isSelected ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                            background: isSelected ? '#eff6ff' : 'white',
                            padding: '16px',
                            transition: 'all 0.2s',
                            boxShadow: isSelected ? '0 4px 6px -1px rgba(59, 130, 246, 0.2)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#bfdbfe';
                              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#e5e7eb';
                              e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                            }
                          }}
                        >
                          <div style={{ position: 'relative' }}>
                            {staffImageSrc ? (
                              <img
                                src={staffImageSrc}
                                alt={staff.name}
                                style={{
                                  width: '64px',
                                  height: '64px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: isSelected ? '3px solid #3b82f6' : '3px solid #e5e7eb',
                                  background: '#f3f4f6',
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '64px',
                                  height: '64px',
                                  borderRadius: '50%',
                                  border: isSelected ? '3px solid #3b82f6' : '3px solid #e5e7eb',
                                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#ffffff',
                                  fontWeight: 600,
                                  fontSize: '18px',
                                }}
                              >
                                {staffInitials}
                              </div>
                            )}
                            {isSelected && (
                              <div style={{
                                position: 'absolute',
                                bottom: '-2px',
                                right: '-2px',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: '#3b82f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid white',
                              }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'center', width: '100%' }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                              {staff.name}
                            </div>
                            {staff.position && (
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                {staff.position}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    type="button"
                    onClick={closeCounselorModal}
                    disabled={counselorModal.submitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#4b5563',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s',
                      background: 'white',
                      cursor: counselorModal.submitting ? 'not-allowed' : 'pointer',
                      opacity: counselorModal.submitting ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!counselorModal.submitting) e.currentTarget.style.background = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      if (!counselorModal.submitting) e.currentTarget.style.background = 'white';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitCounselorChange}
                    disabled={counselorModal.submitting || !counselorModal.selectedStaffId}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      border: 'none',
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'white',
                      background: counselorModal.submitting || !counselorModal.selectedStaffId ? '#93c5fd' : '#3b82f6',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s',
                      cursor: counselorModal.submitting || !counselorModal.selectedStaffId ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!counselorModal.submitting && counselorModal.selectedStaffId) {
                        e.currentTarget.style.background = '#2563eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!counselorModal.submitting && counselorModal.selectedStaffId) {
                        e.currentTarget.style.background = '#3b82f6';
                      }
                    }}
                  >
                    {counselorModal.submitting ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminSalesLayout>
  );
}
